import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@talentech/components";
import type {
  DirectJob,
  DirectJobForm,
  JobListItem,
  JobStatus,
} from "./directJobs.types";

type JobArgs = { tenantId: string; jobId: string };

export const directJobsApi = createApi({
  reducerPath: "directJobs",
  baseQuery: axiosBaseQuery({ baseUrl: process.env.API_URL as string }),
  tagTypes: ["Jobs", "Job"],
  endpoints: (builder) => ({
    getJobs: builder.query<
      JobListItem[],
      { tenantId: string; search?: string; status?: JobStatus }
    >({
      query: ({ tenantId, ...params }) => ({
        url: `/customers/${tenantId}/jobs`,
        params,
      }),
      providesTags: ["Jobs"],
    }),

    getJob: builder.query<DirectJob, JobArgs>({
      query: ({ tenantId, jobId }) => ({
        url: `/customers/${tenantId}/jobs/${jobId}`,
      }),
      providesTags: (_result, _error, { jobId }) => [
        { type: "Job", id: jobId },
      ],
    }),

    createJob: builder.mutation<
      DirectJob,
      { tenantId: string; form: DirectJobForm }
    >({
      query: ({ tenantId, form }) => ({
        url: `/customers/${tenantId}/jobs`,
        method: "POST",
        data: form,
      }),
      invalidatesTags: ["Jobs"],
    }),

    updateJob: builder.mutation<DirectJob, JobArgs & { form: DirectJobForm }>({
      query: ({ tenantId, jobId, form }) => ({
        url: `/customers/${tenantId}/jobs/${jobId}`,
        method: "PUT",
        data: form,
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        "Jobs",
        { type: "Job", id: jobId },
      ],
    }),

    publishJob: builder.mutation<DirectJob, JobArgs>({
      query: ({ tenantId, jobId }) => ({
        url: `/customers/${tenantId}/jobs/${jobId}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        "Jobs",
        { type: "Job", id: jobId },
      ],
    }),

    unpublishJob: builder.mutation<DirectJob, JobArgs>({
      query: ({ tenantId, jobId }) => ({
        url: `/customers/${tenantId}/jobs/${jobId}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        "Jobs",
        { type: "Job", id: jobId },
      ],
    }),

    deleteJob: builder.mutation<void, JobArgs>({
      query: ({ tenantId, jobId }) => ({
        url: `/customers/${tenantId}/jobs/${jobId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        "Jobs",
        { type: "Job", id: jobId },
      ],
    }),

    previewJob: builder.mutation<
      string,
      { tenantId: string; form: DirectJobForm }
    >({
      query: ({ tenantId, form }) => ({
        url: `/customers/${tenantId}/jobs/preview`,
        method: "POST",
        data: form,
      }),
    }),
  }),
});

export const {
  useGetJobsQuery,
  useGetJobQuery,
  useLazyGetJobQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  usePublishJobMutation,
  useUnpublishJobMutation,
  useDeleteJobMutation,
  usePreviewJobMutation,
} = directJobsApi;
